import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Backdrop,
  Button,
  Card,
  Fade,
  InputAdornment,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import { gql } from 'graphql-request';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { Error } from '../../components/Error';
import { UnitForm } from '../../components/Forms';
import Loading from '../../components/Loading';
import { ViewTitle } from '../../components/ViewTitle';
import * as actions from '../../redux/actions';
import { small as ModalStyle } from '../../utils/inlineStyle/Modal';

export default function Units({ title, subtitle }) {
  const dispatch = useDispatch();
  const unit = useSelector((state) => state.unit);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [total, setTotal] = useState();
  const [searchTerm, setSearchTerm] = useState('');
  const [name, setName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm();

  const getAllUnit = async () => {
    try {
      const query = gql`
      query FindUnits {
        findUnits(input:{page:${page},size:${size}, ${
        name ? `name: "${name}"` : ''
      }}){
          currPage,
          lastPage,
          total,
          rows {
          _id
          name
          short_sign
          }
        }
      } 
    `;
      dispatch(actions.unitAllQuery({ query }));
    } catch (error) {
      console.error('Get All Units Error', error);
    }
  };

  useEffect(() => {
    getAllUnit();
    return () => {};
  }, [name, page, size]);

  useEffect(() => {
    setTotal(unit?.total);
    return () => {};
  }, [unit]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setName(searchTerm);
      setPage(1);
    }, 700);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setSize(event.target.value);
    setPage(1);
  };

  const handleDelete = async (id) => {
    try {
      const confirm = window.confirm('ยืนยันการลบหน่วย');
      if (confirm) {
        await dispatch(actions.unitDelete(id));
      }
      getAllUnit();
    } catch (error) {
      alert('ลบหน่วยไม่สำเร็จ');
      console.error('Cannot remove unit', error);
    }
  };

  const onSubmit = async (data) => {
    try {
      await dispatch(actions.unitCreate(data));
      getAllUnit();
      setIsModalOpen(false);
      reset();
    } catch (error) {
      window.alert('เพิ่มเติมหน่วยไม่สำเร็จ');
      console.error(error);
    }
  };

  const renderTitle = () => <ViewTitle title={title} subtitle={subtitle} />;

  const renderModal = () => (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={isModalOpen}>
        <Card sx={ModalStyle} className="max-w-4xl">
          <div className="py-2 font-semibold font-display">
            เพิ่มข้อมูลหน่วย
          </div>
          <UnitForm control={control} errors={errors} />
          <div className="py-2">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-row justify-end gap-1 py-4">
                <Button variant="contained" type="submit">
                  บันทึก
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setIsModalOpen(false)}
                >
                  ยกเลิก
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </Fade>
    </Modal>
  );

  const renderAddButtom = () => (
    <div className="flex flex-row justify-end pb-4">
      <div>
        <Button variant="contained" onClick={() => setIsModalOpen(true)}>
          เพิ่ม
        </Button>
      </div>
    </div>
  );

  const renderSearch = () => (
    <Card className="my-2">
      <div className="p-4 flex flex-row">
        <div className="w-full md:w-1/2">
          <TextField
            label="ค้นหา"
            fullWidth
            size={'small'}
            id="outlined-start-adornment"
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <i className="fas fa-search"></i>
                </InputAdornment>
              ),
            }}
          />
        </div>
      </div>
    </Card>
  );

  const renderTable = () => (
    <Paper>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <div className="font-semibold">ลำดับที่</div>
              </TableCell>
              <TableCell>
                <div className="font-semibold">ชื่อหน่วย</div>
              </TableCell>
              <TableCell>
                <div className="font-semibold">สัญลักษณ์</div>
              </TableCell>
              <TableCell>
                <div className="font-semibold">ดำเนินการ</div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {_.map(unit?.rows, (_unit, index) => (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{_unit?.name}</TableCell>
                <TableCell>{_unit?.short_sign}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Link to={`/km/unit/edit/${_unit?._id}`}>
                      <Button size="small" variant="contained" color="warning">
                        แก้ไข
                      </Button>
                    </Link>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={() => handleDelete(_unit?._id)}
                    >
                      ลบ
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {_.isEmpty(unit?.rows) && (
              <TableRow>
                <TableCell colSpan={5}>
                  <div className="text-center">ไม่มีข้อมูล</div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        page={page - 1}
        //  rowsPerPageOptions={[]}
        onRowsPerPageChange={handleChangeRowsPerPage}
        count={total || 1}
        rowsPerPage={size}
        onPageChange={handleChangePage}
      />
    </Paper>
  );

  if (unit.isLoading) {
    return <Loading />;
  }
  if (!unit.isLoading && unit.isCompleted) {
    return (
      <div>
        <div className="flex flex-wrap justify-between">
          {renderTitle()}
          {renderAddButtom()}
        </div>
        {renderSearch()}
        {renderTable()}
        {renderModal()}
      </div>
    );
  }
  return <Error />;
}

Units.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

Units.defaultProps = {
  title: '',
  subtitle: '',
};
